import { ToolbarButton } from 'components/topics/composer/toolbars/toolbar_button'
import { format } from 'date-fns'
import React, { useCallback, useEffect, useRef } from 'react'

import { ComposerMicIcon } from '../../images'
import { Recorder } from './vmsg'
import { vmsgWasmBase64 } from './vmsg_base64'

export type AudioRecorderProps = {
  editor: any
  isRecording: boolean
  setIsRecording: React.Dispatch<React.SetStateAction<boolean>>
  activeType?: 'message' | 'note'
  desktop?: boolean
}

const base64ToArrayBuffer = (base64Data: string) => {
  const isBrowser =
    typeof window !== 'undefined' && typeof window.atob === 'function'
  const binary = isBrowser
    ? window.atob(base64Data)
    : Buffer.from(base64Data, 'base64').toString('binary')
  const bytes = new Uint8Array(binary.length)

  for (let i = 0; i < binary.length; ++i) {
    bytes[i] = binary.charCodeAt(i)
  }

  return bytes.buffer
}

const shimURL = 'https://unpkg.com/wasm-polyfill.js@0.2.0/wasm-polyfill.js'
const wasmBuffer = base64ToArrayBuffer(vmsgWasmBase64)
const wasmBlob = new window.Blob([wasmBuffer], { type: 'application/wasm' })
const wasmURL = window.URL.createObjectURL(wasmBlob)

export const AudioRecorder = ({
  editor,
  isRecording,
  setIsRecording,
  activeType = 'message',
  desktop = true,
}: AudioRecorderProps) => {
  const recorderRef = useRef<Recorder>(
    new Recorder({
      wasmURL: wasmURL,
      shimURL: shimURL,
    }),
  )

  useEffect(() => {
    if (!isRecording) return

    const recordingTimer = setTimeout(async () => {
      await stopRecording()
    }, 120000)

    return () => clearTimeout(recordingTimer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording])

  const stopRecording = async () => {
    const blob: any = await recorderRef.current.stopRecording()
    setIsRecording(false)
    editor.element.contentEditable = 'true'
    onRecordingComplete(blob)
  }

  const startRecording = async () => {
    await recorderRef.current.initAudio()
    await recorderRef.current.initWorker()
    recorderRef.current.startRecording()
    setIsRecording(true)
    editor.element.contentEditable = 'false'
  }

  const toggleRecording = async () => {
    if (isRecording) {
      await stopRecording()
    } else {
      try {
        await startRecording()
      } catch (err) {
        onRecordingError(err)
      }
    }
  }

  const onRecordingError = useCallback((err: any) => {
    // eslint-disable-next-line no-console
    console.error('error recording', err)
  }, [])

  const onRecordingComplete = (blob: any) => {
    const timeFormat = "MMMdy_hh:mmaaaaa'm'"
    const now = Number(Date.now().toFixed(0))
    blob.lastModifiedDate = now
    blob.name = `Audio_Message_${format(now, timeFormat)}.mp3`
    editor.insertFile(blob)
  }

  return (
    <>
      <ToolbarButton
        onClick={toggleRecording}
        toggled={isRecording}
        title={isRecording ? 'Stop Recording' : 'Record Audio'}
        activeType={activeType}
        desktop={desktop}
      >
        <ComposerMicIcon />
      </ToolbarButton>
    </>
  )
}
